import { addDays, format, parseISO } from 'date-fns'
import type { ScheduleDog, ScheduleGuide } from './schedule'

export type SeriesSpan = {
  scheduleDate: string
  durationDays?: number
  excludedDates?: string[]
}

export function spanDates(scheduleDate: string, durationDays?: number): string[] {
  const start = parseISO(scheduleDate)
  if (Number.isNaN(start.getTime())) return [scheduleDate]

  const span = Math.max(durationDays ?? 1, 1)
  return Array.from({ length: span }, (_, day) => format(addDays(start, day), 'yyyy-MM-dd'))
}

export function effectiveSpanDates(span: SeriesSpan): string[] {
  const excluded = new Set(span.excludedDates ?? [])
  return spanDates(span.scheduleDate, span.durationDays).filter((date) => !excluded.has(date))
}

export function isMultiDaySeries(span: SeriesSpan): boolean {
  return effectiveSpanDates(span).length > 1
}

export function coversDate(span: SeriesSpan, date: string): boolean {
  return effectiveSpanDates(span).includes(date)
}

export function dayNumberOf(span: SeriesSpan, date: string): number {
  const index = effectiveSpanDates(span).indexOf(date)
  return index < 0 ? 1 : index + 1
}

export function formatSpanRange(span: SeriesSpan): string {
  const dates = effectiveSpanDates(span)
  if (dates.length === 0) return ''
  if (dates.length === 1) return format(parseISO(dates[0]), 'MMM d')

  const first = parseISO(dates[0])
  const last = parseISO(dates[dates.length - 1])
  const sameMonth = format(first, 'MMM') === format(last, 'MMM')

  return `${format(first, 'MMM d')} – ${format(last, sameMonth ? 'd' : 'MMM d')}`
}

export function formatDayLabel(date: string): string {
  return format(parseISO(date), 'EEE, MMM d')
}

function scopedToDate<T extends { dayDate?: string }>(rows: T[], dayDate?: string): T[] {
  const dated = rows.filter((row) => row.dayDate !== undefined)
  if (dated.length === 0) return rows
  return dayDate ? dated.filter((row) => row.dayDate === dayDate) : dated
}

function distinctBy<T>(rows: T[], key: (row: T) => string): T[] {
  const seen = new Set<string>()
  return rows.filter((row) => {
    const id = key(row)
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
}

export function guidesForDate<T extends Pick<ScheduleGuide, 'guideId' | 'dayDate'>>(
  guides: T[] | undefined,
  dayDate?: string,
): T[] {
  return distinctBy(scopedToDate(guides ?? [], dayDate), (guide) => guide.guideId)
}

export function dogsForDate<T extends Pick<ScheduleDog, 'dogId' | 'dayDate'>>(
  dogs: T[] | undefined,
  dayDate?: string,
): T[] {
  return distinctBy(scopedToDate(dogs ?? [], dayDate), (dog) => dog.dogId)
}
